import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  DollarSign, 
  TrendingUp,
  Calendar,
  CreditCard,
  LogOut,
  ArrowLeft,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import './Financials.css';

const Financials = () => {
  const { user, logout } = useContext(AuthContext);
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchFinancials = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/business/financials`, { headers });
      setFinancials(response.data);
    } catch (error) {
      toast.error('Failed to fetch financial data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFinancials();
  }, [fetchFinancials]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="financials-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-name">{user?.business_name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button 
              className="btn btn-secondary" 
              onClick={handleLogout}
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-container">
          {/* Back to Dashboard */}
          <button 
            className="back-btn"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={18} /> Back to Dashboard
          </button>

          {/* Page Title */}
          <div className="page-header">
            <Receipt size={32} />
            <h1>Business Financials</h1>
            <p>Track your subscription and platform fees</p>
          </div>

          {/* Financial Summary Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{background: 'rgba(96, 165, 250, 0.1)'}}>
                <TrendingUp size={24} color="#60a5fa" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Total Transaction Volume</p>
                <p className="stat-value">${financials?.total_transaction_volume?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{background: 'rgba(251, 191, 36, 0.1)'}}>
                <DollarSign size={24} color="#fbbf24" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Platform Fees (1%)</p>
                <p className="stat-value">${financials?.platform_fees_accumulated?.toFixed(2) || '0.00'}</p>
                <p className="stat-description">Accumulated from {financials?.total_transaction_volume > 0 ? 'completed transactions' : 'transactions'}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{background: 'rgba(168, 85, 247, 0.1)'}}>
                <Calendar size={24} color="#a855f7" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Subscription Fees</p>
                <p className="stat-value">${financials?.subscription_fee_owed?.toFixed(2) || '0.00'}</p>
                <p className="stat-description">{financials?.months_active} months active ($10/month)</p>
              </div>
            </div>

            <div className="stat-card highlight-card">
              <div className="stat-icon" style={{background: 'rgba(239, 68, 68, 0.1)'}}>
                <CreditCard size={24} color="#ef4444" />
              </div>
              <div className="stat-info">
                <p className="stat-label">Total Amount Owed</p>
                <p className="stat-value total-owed">${financials?.total_owed?.toFixed(2) || '0.00'}</p>
                <p className="stat-description">Platform fees + Subscription</p>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="details-section">
            <h2 className="section-title">Subscription Details</h2>
            <div className="details-card">
              <div className="detail-row">
                <span className="detail-label">Business Name:</span>
                <span className="detail-value">{financials?.business_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{financials?.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Subscription Status:</span>
                <span className={`status-badge status-${financials?.subscription_status}`}>
                  {financials?.subscription_status}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Months Active:</span>
                <span className="detail-value">{financials?.months_active} months</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Payment:</span>
                <span className="detail-value">
                  {financials?.last_subscription_payment 
                    ? new Date(financials.last_subscription_payment).toLocaleDateString()
                    : 'No payment yet'}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Notice */}
          <div className="notice-card">
            <div className="notice-icon">ℹ️</div>
            <div className="notice-content">
              <h3>Payment Information</h3>
              <p>
                You will receive automated monthly invoices via email for your subscription and accumulated platform fees. 
                Invoices are sent at the beginning of each month.
              </p>
              <p className="notice-subtext">
                Questions about billing? Contact support@crypto-cashier.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Financials;
;
